import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import { useHistory, useParams } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import { Button } from "@mui/material";
import { GET_ALL_EXAM_PAPERS_DELIVERY } from "utils/graphql/services/ai-evaluation/queries";
import autoGradeClient from "utils/graphql/autoGradeClient";

type ExamPaperDelivery = {
  examPaperId: number;
  sortOrder: number;
  collegeIds: string[];
  collegeNames: string[];
  acadamicYearIds: string[];
  academicYearNames: string[];
  batchIds: string[];
  batchNames: string[];
  classIds: string[];
  classNames: string[];
  boardIds: string[];
  boardNames: string[];
};

function ViewExamPaperDeliveryPage() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const { data, loading, error } = useQuery(GET_ALL_EXAM_PAPERS_DELIVERY, {
    variables: {
      input: {
        id: Number(id),
      },
    },
    client: autoGradeClient,
  });

  const [form, setForm] = useState<ExamPaperDelivery>({
    examPaperId: 0,
    sortOrder: 1,
    collegeIds: [],
    collegeNames: [],
    acadamicYearIds: [],
    academicYearNames: [],
    batchIds: [],
    batchNames: [],
    classIds: [],
    classNames: [],
    boardIds: [],
    boardNames: [],
  });

  useEffect(() => {
    if (data?.getExamPaperDeliveries?.examPaperDeliveries?.length > 0) {
      const delivery = data.getExamPaperDeliveries.examPaperDeliveries[0];
      setForm({
        examPaperId: delivery.examPaperId,
        sortOrder: delivery.sortOrder,
        collegeIds: delivery.collegeIds,
        collegeNames: delivery.collegeNames,
        acadamicYearIds: delivery.academicYearIds,
        academicYearNames: delivery.academicYearNames,
        batchIds: delivery.batchIds,
        batchNames: delivery.batchNames,
        classIds: delivery.classIds,
        classNames: delivery.classNames,
        boardIds: delivery.boardIds,
        boardNames: delivery.boardNames,
      });
    }
  }, [data]);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error.message}</Typography>;

  const renderMultiSelect = (
    label: string,
    values: string[],
    names: string[]
  ) => (
    <FormControl fullWidth sx={{ mt: 1 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={values}
        input={<OutlinedInput label={label} />}
        renderValue={() => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {names.map((name, idx) => (
              <Chip key={idx} label={name} />
            ))}
          </Box>
        )}
        disabled
      >
        {values.map((id, idx) => (
          <MenuItem key={id} value={id}>
            {names[idx]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Paper elevation={3} style={{ padding: 16 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">View Exam Paper Delivery</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Exam Paper"
                type="number"
                value={form.examPaperId}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                value={form.sortOrder}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {renderMultiSelect("College", form.collegeIds, form.collegeNames)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderMultiSelect(
                "Academic Year",
                form.acadamicYearIds,
                form.academicYearNames
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderMultiSelect("Batch", form.batchIds, form.batchNames)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderMultiSelect("Class", form.classIds, form.classNames)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderMultiSelect("Board", form.boardIds, form.boardNames)}
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button variant="outlined" onClick={() => history.goBack()}>
                  Close
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default ViewExamPaperDeliveryPage;
